// src/pages/RegisterPage/RegisterPage.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import QRCode from 'qrcode';
import { jsPDF } from 'jspdf';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LEKudluLogo from '../../assets/images/LEKudluLogoTSP.svg';
import './RegisterPage.css';

const RegisterPage = () => {
    const [formData, setFormData] = useState({
        parentName: '',
        phone: '',
        childName: '',
        email: ''
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [qrDataURL, setQrDataURL] = useState('');
    const [passCanvas, setPassCanvas] = useState(null);
    const [registrationId, setRegistrationId] = useState('');

    // Hide TARS chatbot on carnival pages
    useEffect(() => {
        const tarsWidget = document.querySelector('[id*="tars"]') ||
                          document.querySelector('[class*="tars"]') ||
                          document.querySelector('iframe[src*="tars"]');
        if (tarsWidget) {
            tarsWidget.style.display = 'none';
        }

        return () => {
            // Re-show on unmount if it exists
            if (tarsWidget) {
                tarsWidget.style.display = '';
            }
        };
    }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const generateUniqueId = () => {
        const timestamp = Date.now().toString(36);
        const randomStr = Math.random().toString(36).substring(2, 9);
        return `DC2025-${timestamp}-${randomStr}`.toUpperCase();
    };

    const generateQRCode = async (registrationId) => {
        try {
            const qrData = JSON.stringify({
                id: registrationId,
                parentName: formData.parentName,
                childName: formData.childName,
                event: 'Deepawali Carnival 2025',
                venue: 'Little Elly Kudlu'
            });

            const qrCodeDataURL = await QRCode.toDataURL(qrData, {
                width: 400,
                margin: 2,
                color: {
                    dark: '#1A1423',
                    light: '#FFFFFF'
                }
            });

            return qrCodeDataURL;
        } catch (err) {
            console.error('QR Generation Error:', err);
            throw new Error('Failed to generate QR code');
        }
    };

    const generatePassCanvas = async (registrationId, qrCode) => {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            // Mobile-friendly 9:16 aspect ratio, scaled for quality
            canvas.width = 1080;
            canvas.height = 1920;
            const ctx = canvas.getContext('2d');

            // Background gradient
            const bgGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
            bgGradient.addColorStop(0, '#1A1423');
            bgGradient.addColorStop(0.3, '#5B2C6F');
            bgGradient.addColorStop(0.6, '#C2185B');
            bgGradient.addColorStop(1, '#FF6B35');
            ctx.fillStyle = bgGradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Decorative border
            ctx.strokeStyle = '#FDB750';
            ctx.lineWidth = 12;
            ctx.strokeRect(40, 40, canvas.width - 80, canvas.height - 80);

            // Inner border
            ctx.strokeStyle = 'rgba(253, 183, 80, 0.4)';
            ctx.lineWidth = 8;
            ctx.strokeRect(60, 60, canvas.width - 120, canvas.height - 120);

            // Load and draw LE Kudlu logo at top
            const logoImg = new Image();
            logoImg.crossOrigin = 'anonymous';
            logoImg.onload = () => {
                // Draw logo at top center - SMALLER, MORE SPACE
                const logoSize = 110;
                const logoX = (canvas.width - logoSize) / 2;
                const logoY = 100;
                ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);

                // Title - Deepawali Carnival - MORE SPACE FROM LOGO
                const headerY = 280;
                ctx.fillStyle = '#FDB750';
                ctx.font = 'bold 70px Arial, sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText('Deepawali Carnival', canvas.width / 2, headerY);

                ctx.fillStyle = '#FFFFFF';
                ctx.font = 'bold 38px Arial, sans-serif';
                ctx.fillText('2025', canvas.width / 2, headerY + 52);

                // Diya emoji decorations - MOVED DOWN, NOT CLASHING
                ctx.font = '48px Arial';
                ctx.fillText('🪔', 150, headerY + 15);
                ctx.fillText('🪔', canvas.width - 150, headerY + 15);

                // Guest details section - MORE SPACE
                const detailsY = 420;
                ctx.fillStyle = 'rgba(45, 27, 61, 0.9)';
                roundRect(ctx, 100, detailsY, canvas.width - 200, 450, 30, true);

                ctx.fillStyle = '#FDB750';
                ctx.font = 'bold 48px Arial, sans-serif';
                ctx.textAlign = 'left';
                ctx.fillText('◆ Guest Details', 160, detailsY + 70);

                ctx.fillStyle = '#FFFFFF';
                ctx.font = '38px Arial, sans-serif';

                let yOffset = detailsY + 150;
                ctx.fillText('Parent:', 160, yOffset);
                ctx.font = 'bold 38px Arial, sans-serif';
                ctx.fillStyle = '#FDB750';
                ctx.fillText(formData.parentName, 160, yOffset + 50);

                yOffset += 130;
                ctx.fillStyle = '#FFFFFF';
                ctx.font = '38px Arial, sans-serif';
                ctx.fillText('Child:', 160, yOffset);
                ctx.font = 'bold 38px Arial, sans-serif';
                ctx.fillStyle = '#FDB750';
                ctx.fillText(formData.childName, 160, yOffset + 50);

                yOffset += 130;
                ctx.fillStyle = '#FFFFFF';
                ctx.font = '32px Arial, sans-serif';
                ctx.fillText(`ID: ${registrationId}`, 160, yOffset);

                // Event details section
                const eventY = 900;
                ctx.fillStyle = 'rgba(255, 107, 53, 0.3)';
                roundRect(ctx, 100, eventY, canvas.width - 200, 280, 30, true);

                ctx.fillStyle = '#FDB750';
                ctx.font = 'bold 44px Arial, sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText('◇ Event Details', canvas.width / 2, eventY + 60);

                ctx.fillStyle = '#FFFFFF';
                ctx.font = '36px Arial, sans-serif';
                ctx.fillText('November 1st, 2025 | 10:30 AM onwards', canvas.width / 2, eventY + 130);

                ctx.font = 'bold 38px Arial, sans-serif';
                ctx.fillStyle = '#FDB750';
                ctx.fillText('Little Elly Kudlu Campus', canvas.width / 2, eventY + 200);

                // QR Code section - PROPER SPACING
                const qrY = 1230;
                const qrSize = 350;
                const qrX = (canvas.width - qrSize) / 2;

                // QR background
                ctx.fillStyle = '#FFFFFF';
                ctx.shadowColor = 'rgba(253, 183, 80, 0.6)';
                ctx.shadowBlur = 30;
                roundRect(ctx, qrX - 15, qrY - 15, qrSize + 30, qrSize + 30, 20, true);
                ctx.shadowBlur = 0;

                // Draw QR code
                const qrImage = new Image();
                qrImage.onload = () => {
                    ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

                    // Scan instruction - BELOW QR with proper spacing
                    ctx.fillStyle = '#FFFFFF';
                    ctx.font = '32px Arial, sans-serif';
                    ctx.textAlign = 'center';
                    ctx.fillText('Scan at venue entrance', canvas.width / 2, qrY + qrSize + 55);

                    // Footer section - MUCH MORE SPACE
                    const footerY = canvas.height - 130;

                    // Logo and organized by
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
                    ctx.font = '28px Arial, sans-serif';
                    ctx.fillText('Organized by Little Elly Kudlu', canvas.width / 2, footerY);

                    // Neuralway.ai branding - SEPARATED
                    ctx.fillStyle = 'rgba(253, 183, 80, 0.7)';
                    ctx.font = 'bold 24px Arial, sans-serif';
                    ctx.fillText('Crafted by neuralway.ai', canvas.width / 2, footerY + 50);

                    resolve(canvas);
                };
                qrImage.src = qrCode;
            };

            // Use the imported logo
            logoImg.src = LEKudluLogo;
        });
    };

    // Helper function to draw rounded rectangles
    const roundRect = (ctx, x, y, width, height, radius, fill) => {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
        if (fill) ctx.fill();
    };

    const downloadAsImage = async (canvas, filename) => {
        const link = document.createElement('a');
        link.download = filename;
        link.href = canvas.toDataURL('image/png', 1.0);
        link.click();
    };

    const generatePDF = async (canvas) => {
        const imgData = canvas.toDataURL('image/png', 1.0);

        // A4 size in portrait mode
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const pdfWidth = 210;
        const pdfHeight = 297;

        // Calculate dimensions to fit properly
        // Pass is 1080x1920 (9:16 ratio)
        const passRatio = canvas.height / canvas.width;

        // Use 90% of page width for margins
        const imgWidth = pdfWidth * 0.9;
        const imgHeight = imgWidth * passRatio;

        // Center horizontally and vertically
        const x = (pdfWidth - imgWidth) / 2;
        const y = Math.max(5, (pdfHeight - imgHeight) / 2);

        // If height exceeds page, scale down to fit
        if (imgHeight > pdfHeight - 10) {
            const scaledHeight = pdfHeight - 10;
            const scaledWidth = scaledHeight / passRatio;
            const scaledX = (pdfWidth - scaledWidth) / 2;
            pdf.addImage(imgData, 'PNG', scaledX, 5, scaledWidth, scaledHeight);
        } else {
            pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
        }

        pdf.save(`Deepawali_Carnival_Pass_${formData.parentName.replace(/\s+/g, '_')}.pdf`);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Validate form
            if (!formData.parentName || !formData.phone || !formData.childName) {
                throw new Error('Please fill all required fields');
            }

            // Validate phone
            const phoneRegex = /^[6-9]\d{9}$/;
            if (!phoneRegex.test(formData.phone)) {
                throw new Error('Please enter a valid 10-digit phone number');
            }

            // Generate unique registration ID
            const regId = generateUniqueId();
            setRegistrationId(regId);

            // Generate QR Code
            const qrCode = await generateQRCode(regId);
            setQrDataURL(qrCode);

            // Generate beautiful pass canvas
            const canvas = await generatePassCanvas(regId, qrCode);
            setPassCanvas(canvas);

            // Save to Firestore
            await addDoc(collection(db, 'carnival_registrations'), {
                registrationId: regId,
                parentName: formData.parentName,
                childName: formData.childName,
                phone: formData.phone,
                email: formData.email,
                registeredAt: new Date().toISOString(),
                checkedIn: false,
                checkedInAt: null
            });

            setSuccess(true);
        } catch (err) {
            console.error('Registration Error:', err);
            setError(err.message || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = () => {
        if (passCanvas) {
            generatePDF(passCanvas);
        }
    };

    const handleDownloadImage = () => {
        if (passCanvas) {
            downloadAsImage(passCanvas, `Deepawali_Carnival_Pass_${formData.parentName.replace(/\s+/g, '_')}.png`);
        }
    };

    if (success) {
        return (
            <div className="register-page success-page">
                <motion.div
                    className="success-container"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', duration: 0.6 }}
                >
                    <motion.div
                        className="success-icon"
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 1 }}
                    >
                        ✓
                    </motion.div>
                    <h1>Registration Successful!</h1>
                    <p className="success-subtitle">Your Deepawali Carnival pass is ready!</p>

                    {passCanvas && (
                        <div className="pass-preview">
                            <img
                                src={passCanvas.toDataURL('image/png')}
                                alt="Carnival Pass"
                                className="pass-image"
                            />
                        </div>
                    )}

                    <div className="download-options">
                        <h3>◆ Download Your Pass</h3>
                        <p className="download-info">Choose your preferred format:</p>

                        <div className="download-buttons">
                            <motion.button
                                className="download-button download-image"
                                onClick={handleDownloadImage}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <span className="button-icon">◆</span>
                                <div className="button-content">
                                    <span className="button-title">Download as Image</span>
                                    <span className="button-subtitle">Save to gallery (recommended)</span>
                                </div>
                            </motion.button>

                            <motion.button
                                className="download-button download-pdf"
                                onClick={handleDownloadPDF}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <span className="button-icon">◇</span>
                                <div className="button-content">
                                    <span className="button-title">Download as PDF</span>
                                    <span className="button-subtitle">Print or email</span>
                                </div>
                            </motion.button>
                        </div>
                    </div>

                    <div className="success-details">
                        <h3>What's Next?</h3>
                        <ul>
                            <li>◆ Download your pass using one of the buttons above</li>
                            <li>◇ Save it to your phone's gallery</li>
                            <li>◈ Show the QR code at the venue entrance</li>
                            <li>✦ Enjoy the Deepawali Carnival!</li>
                        </ul>
                    </div>

                    <div className="registration-info">
                        <p><strong>Registration ID:</strong> {registrationId}</p>
                        <p><strong>Parent:</strong> {formData.parentName}</p>
                        <p><strong>Child:</strong> {formData.childName}</p>
                    </div>

                    <motion.button
                        className="back-button"
                        onClick={() => window.location.href = '/deepawalicarnival'}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        ← Back to Carnival Page
                    </motion.button>

                    <div className="crafted-by-footer">
                        <p>Crafted by <a href="https://neuralway.ai" target="_blank" rel="noopener noreferrer">neuralway.ai</a></p>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="register-page">
            <motion.div
                className="register-container"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <div className="register-header">
                    <img src={LEKudluLogo} alt="Little Elly Kudlu" className="register-logo" />
                    <h1>Register for Deepawali Carnival</h1>
                    <p>November 1st, 2025 | 10:30 AM onwards</p>
                </div>

                <form onSubmit={handleSubmit} className="register-form">
                    <div className="form-group">
                        <label htmlFor="parentName">Parent/Guardian Name *</label>
                        <input
                            type="text"
                            id="parentName"
                            name="parentName"
                            value={formData.parentName}
                            onChange={handleChange}
                            placeholder="Enter parent/guardian name"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="childName">Child's Name *</label>
                        <input
                            type="text"
                            id="childName"
                            name="childName"
                            value={formData.childName}
                            onChange={handleChange}
                            placeholder="Enter child's name"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone">Phone Number *</label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="10-digit mobile number"
                            maxLength="10"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email (Optional)</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your.email@example.com"
                        />
                    </div>

                    {error && (
                        <motion.div
                            className="error-message"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            {error}
                        </motion.div>
                    )}

                    <motion.button
                        type="submit"
                        className="submit-button"
                        disabled={loading}
                        whileHover={{ scale: loading ? 1 : 1.02 }}
                        whileTap={{ scale: loading ? 1 : 0.98 }}
                    >
                        {loading ? (
                            <>
                                <span className="spinner"></span>
                                Generating Pass...
                            </>
                        ) : (
                            'Generate My Pass'
                        )}
                    </motion.button>
                </form>

                <div className="register-footer">
                    <p>Your digital pass with QR code will be downloaded automatically</p>
                    <button
                        className="back-link"
                        onClick={() => window.location.href = '/deepawalicarnival'}
                    >
                        ← Back to Carnival
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

export default RegisterPage;
